import { ItemBar } from "components/ItemBar";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ToastContext } from "context/ToastContext";
import useDocumentTitle from "hooks/useDocumentTitle";
import { Badge } from "primereact/badge";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { useContext, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetContractHierarchyRulesByContractQuery,
  useCreateContractHierarchyRuleMutation,
  useUpdateContractHierarchyRuleMutation,
  useDeleteContractHierarchyRuleMutation,
} from "store/queries/contractHierarchyRules";
import { useGetAllPackageRulesetsQuery } from "store/queries/packageRuleset";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetCaseRateRulesetsQuery } from "store/queries/pricing";
import { useGetAllDiscountRulesetsQuery } from "store/queries/procedureDiscountRuleset";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";
import {
  IContractHierarchyRule,
  IContractHierarchyRuleSuppression,
  ISuppressionType,
  ITriggerRulesetType,
} from "types/ContractHierarchyRule/ContractHierarchyRule";

import { HIERARCHY_RULES_TAB_INDEX } from "../PayerRules/utils/constants";

const SuppressionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

interface IRulesetOption {
  label: string;
  value: string;
  rulesetType: ITriggerRulesetType;
}

const suppressionTypeOptions = [
  { label: "Not Paid (100% discount)", value: "NOT_PAID" },
  { label: "Pay Both (remove adjustment)", value: "PAY_BOTH" },
];

export function HierarchyRulesetPage() {
  const { providerId, payerId } = useParams<{
    providerId: string;
    payerId: string;
  }>();
  const isCreateMode = !providerId || !payerId;
  const navigate = useNavigate();
  const { current: toastElement } = useContext(ToastContext);

  useDocumentTitle(
    isCreateMode ? "New Hierarchy Ruleset" : "Edit Hierarchy Ruleset"
  );

  // Create mode selections
  const [selectedProviderId, setSelectedProviderId] = useState<string>("");
  const [selectedPayerId, setSelectedPayerId] = useState<number | null>(null);

  // Effective IDs (from URL params or create mode selections)
  const effectiveProviderId = isCreateMode ? selectedProviderId : providerId;
  const effectivePayerId = isCreateMode ? selectedPayerId : Number(payerId);
  const hasSelection = !!effectiveProviderId && !!effectivePayerId;

  // Local editable copy of rules
  const [localRules, setLocalRules] = useState<IContractHierarchyRule[]>([]);

  // DataTable row expansion
  const [expandedRows, setExpandedRows] = useState<any>(null);

  // Add rule form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newRuleRulesetId, setNewRuleRulesetId] = useState<string | null>(null);
  const [newRulePriority, setNewRulePriority] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Queries
  const { data: providersData } = useGetAllProvidersQuery(undefined, {
    skip: !isCreateMode,
  });
  const { data: payersData } = useGetAllPayersQuery(undefined, {
    skip: !isCreateMode,
  });

  const { data: rulesResponse, isLoading: isLoadingRules } =
    useGetContractHierarchyRulesByContractQuery(
      {
        providerId: effectiveProviderId || "",
        payerId: effectivePayerId || 0,
      },
      { skip: !hasSelection }
    );

  const serverRules = rulesResponse?.data || [];

  // Sync local state from server data and expand all rows by default
  useEffect(() => {
    setLocalRules(serverRules.map((r) => ({ ...r })));
    const expanded: Record<string, boolean> = {};
    serverRules.forEach((r) => {
      expanded[r.id] = true;
    });
    setExpandedRows(expanded);
  }, [serverRules]);

  const { data: mpprRulesets } = useGetAllDiscountRulesetsQuery(undefined, {
    skip: !hasSelection,
  });
  const { data: caseRateRulesets } = useGetCaseRateRulesetsQuery(undefined, {
    skip: !hasSelection,
  });
  const { data: packageRulesets } = useGetAllPackageRulesetsQuery(undefined, {
    skip: !hasSelection,
  });

  // Mutations
  const [createRule, { isLoading: isCreating }] =
    useCreateContractHierarchyRuleMutation();
  const [updateRule] = useUpdateContractHierarchyRuleMutation();
  const [deleteRule] = useDeleteContractHierarchyRuleMutation();

  // Dirty check — compare local edits to server data
  const isDirty = useMemo(() => {
    if (localRules.length !== serverRules.length) return true;
    return localRules.some((local) => {
      const server = serverRules.find((s) => s.id === local.id);
      if (!server) return true;
      if (local.active !== server.active) return true;
      if (local.priority !== server.priority) return true;
      if (
        JSON.stringify(local.suppressions) !==
        JSON.stringify(server.suppressions)
      )
        return true;
      return false;
    });
  }, [localRules, serverRules]);

  // Provider/payer names for header
  const providerName = useMemo(() => {
    if (!isCreateMode && serverRules.length > 0) {
      return serverRules[0].provider?.name || effectiveProviderId;
    }
    if (isCreateMode && providersData?.data) {
      return (
        providersData.data.find((p: any) => p.id === effectiveProviderId)
          ?.name || effectiveProviderId
      );
    }
    return effectiveProviderId || "";
  }, [isCreateMode, serverRules, providersData, effectiveProviderId]);

  const payerName = useMemo(() => {
    if (!isCreateMode && serverRules.length > 0) {
      return serverRules[0].payer?.name || effectivePayerId;
    }
    if (isCreateMode && payersData?.data) {
      return (
        payersData.data.find((p: any) => p.id === effectivePayerId)?.name ||
        effectivePayerId
      );
    }
    return effectivePayerId || "";
  }, [isCreateMode, serverRules, payersData, effectivePayerId]);

  const providerOptions = useMemo(() => {
    if (!providersData?.data) return [];
    return providersData.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [providersData]);

  const payerOptions = useMemo(() => {
    if (!payersData?.data) return [];
    return payersData.data.map((p: any) => ({ label: p.name, value: p.id }));
  }, [payersData]);

  // All rulesets for this provider+payer
  const rulesetOptions: IRulesetOption[] = useMemo(() => {
    if (!hasSelection) return [];
    const options: IRulesetOption[] = [];

    if (mpprRulesets?.data) {
      mpprRulesets.data
        .filter(
          (r: any) =>
            r.providerId === effectiveProviderId &&
            r.payerId === effectivePayerId
        )
        .forEach((r: any) => {
          options.push({
            label: `${r.groupName || "MPPR Ruleset"} (MPPR)`,
            value: r.id,
            rulesetType: "MPPR",
          });
        });
    }

    if (caseRateRulesets?.data) {
      caseRateRulesets.data
        .filter(
          (r: any) =>
            r.providerId === effectiveProviderId &&
            r.payerId === effectivePayerId
        )
        .forEach((r: any) => {
          options.push({
            label: `${r.groupName || "Case Rate"} (Case Rate)`,
            value: r.id,
            rulesetType: "Case Rate",
          });
        });
    }

    if (packageRulesets?.data) {
      packageRulesets.data
        .filter(
          (r: any) =>
            r.providerId === effectiveProviderId &&
            r.payerId === effectivePayerId
        )
        .forEach((r: any) => {
          options.push({
            label: `${r.groupName || "Package"} (Package)`,
            value: r.id,
            rulesetType: "Package",
          });
        });
    }

    return options;
  }, [
    hasSelection,
    effectiveProviderId,
    effectivePayerId,
    mpprRulesets,
    caseRateRulesets,
    packageRulesets,
  ]);

  const resolveRulesetName = (rulesetId: string): string => {
    const option = rulesetOptions.find((o) => o.value === rulesetId);
    return option?.label || rulesetId;
  };

  // Rulesets already used by existing hierarchy rules
  const usedRulesetIds = useMemo(() => {
    return new Set(localRules.map((r) => r.triggerRulesetId));
  }, [localRules]);

  const availableForNewRule = useMemo(() => {
    return rulesetOptions.filter((o) => !usedRulesetIds.has(o.value));
  }, [rulesetOptions, usedRulesetIds]);

  const nextPriority = useMemo(() => {
    if (localRules.length === 0) return 1;
    return Math.max(...localRules.map((r) => r.priority)) + 1;
  }, [localRules]);

  // --- Local state handlers (no API calls) ---

  const handleToggleActive = (ruleId: string) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, active: !r.active } : r))
    );
  };

  const handlePriorityChange = (ruleId: string, value: number) => {
    setLocalRules((prev) =>
      prev.map((r) => (r.id === ruleId ? { ...r, priority: value } : r))
    );
  };

  const handleSuppressionTypeChange = (
    ruleId: string,
    index: number,
    newType: ISuppressionType
  ) => {
    setLocalRules((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) return r;
        const updated = [...r.suppressions];
        updated[index] = { ...updated[index], suppressionType: newType };
        return { ...r, suppressions: updated };
      })
    );
  };

  const handleRemoveSuppression = (ruleId: string, index: number) => {
    setLocalRules((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) return r;
        return {
          ...r,
          suppressions: r.suppressions.filter((_, i) => i !== index),
        };
      })
    );
  };

  const handleAddSuppression = (ruleId: string, rulesetId: string) => {
    const option = rulesetOptions.find((o) => o.value === rulesetId);
    if (!option) return;

    const newSuppression: IContractHierarchyRuleSuppression = {
      rulesetType: option.rulesetType,
      rulesetId: option.value,
      suppressionType: "NOT_PAID",
    };
    setLocalRules((prev) =>
      prev.map((r) => {
        if (r.id !== ruleId) return r;
        return { ...r, suppressions: [...r.suppressions, newSuppression] };
      })
    );
  };

  // --- Immediate API actions (delete, create) ---

  const handleDeleteRule = (rule: IContractHierarchyRule) => {
    confirmDialog({
      message: `Delete hierarchy rule "${rule.name}"?`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        try {
          await deleteRule(rule.id).unwrap();
          toastElement?.show({
            severity: "success",
            summary: "Deleted",
            detail: "Hierarchy rule deleted",
          });
        } catch (error: any) {
          toastElement?.show({
            severity: "error",
            summary: "Error",
            detail: error?.data?.message || "Failed to delete rule",
          });
        }
      },
    });
  };

  const handleOpenAddForm = () => {
    setNewRulePriority(nextPriority);
    setNewRuleRulesetId(null);
    setShowAddForm(true);
  };

  const handleCancelAddForm = () => {
    setShowAddForm(false);
    setNewRuleRulesetId(null);
    setNewRulePriority(null);
  };

  const handleCreateRule = async () => {
    if (!newRuleRulesetId || !newRulePriority || !hasSelection) return;
    const option = rulesetOptions.find((o) => o.value === newRuleRulesetId);
    if (!option) return;

    try {
      await createRule({
        providerId: effectiveProviderId!,
        payerId: effectivePayerId!,
        name: option.label,
        triggerRulesetType: option.rulesetType,
        triggerRulesetId: option.value,
        priority: newRulePriority,
        active: true,
        suppressions: [],
      }).unwrap();

      toastElement?.show({
        severity: "success",
        summary: "Created",
        detail: "New hierarchy rule created",
      });
      handleCancelAddForm();

      // In create mode, transition to edit mode after first rule
      if (isCreateMode) {
        navigate(
          `/payer-rules/hierarchy-rules/ruleset/${effectiveProviderId}/${effectivePayerId}`,
          { replace: true }
        );
      }
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to create rule",
      });
    }
  };

  // --- Save all changes ---

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updates = localRules
        .filter((local) => {
          const server = serverRules.find((s) => s.id === local.id);
          if (!server) return false;
          return (
            local.active !== server.active ||
            local.priority !== server.priority ||
            JSON.stringify(local.suppressions) !==
              JSON.stringify(server.suppressions)
          );
        })
        .map((local) =>
          updateRule({
            id: local.id,
            active: local.active,
            priority: local.priority,
            suppressions: local.suppressions,
          }).unwrap()
        );

      await Promise.all(updates);
      toastElement?.show({
        severity: "success",
        summary: "Saved",
        detail: "All changes saved successfully",
      });
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: error?.data?.message || "Failed to save changes",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setLocalRules(serverRules.map((r) => ({ ...r })));
  };

  const handleBack = () => {
    navigate(`/payer-rules?tab=${HIERARCHY_RULES_TAB_INDEX}`);
  };

  // --- Column templates ---

  const nameBodyTemplate = (row: IContractHierarchyRule) => (
    <div className="flex align-items-center gap-2">
      <span>{row.name}</span>
      <Badge value={row.triggerRulesetType} severity="info" />
    </div>
  );

  const priorityBodyTemplate = (row: IContractHierarchyRule) => (
    <InputNumber
      value={row.priority}
      onValueChange={(e) => {
        if (e.value !== null && e.value !== undefined) {
          handlePriorityChange(row.id, e.value);
        }
      }}
      min={1}
      className="w-5rem"
      inputClassName="w-5rem"
    />
  );

  const activeBodyTemplate = (row: IContractHierarchyRule) => (
    <InputSwitch
      checked={row.active}
      onChange={() => handleToggleActive(row.id)}
    />
  );

  const actionsBodyTemplate = (row: IContractHierarchyRule) => (
    <Button
      icon="pi pi-trash"
      className="p-button-danger p-button-text p-button-sm"
      onClick={() => handleDeleteRule(row)}
    />
  );

  // --- Row expansion template (suppressions) ---

  const rowExpansionTemplate = (row: IContractHierarchyRule) => {
    const availableForSuppression = rulesetOptions.filter(
      (o) =>
        !row.suppressions.some((s) => s.rulesetId === o.value) &&
        o.value !== row.triggerRulesetId
    );

    const suppressedBy = localRules.filter(
      (other) =>
        other.id !== row.id &&
        other.suppressions.some((sup) => sup.rulesetId === row.triggerRulesetId)
    );

    return (
      <div className="grid p-3">
        <div className="col-6">
          <h4 className="mt-0 mb-2">Suppresses</h4>
          {row.suppressions.length === 0 && (
            <span className="text-500">No suppressions configured.</span>
          )}
          {row.suppressions.map(
            (sup: IContractHierarchyRuleSuppression, idx: number) => (
              <SuppressionRow key={`${sup.rulesetId}-${idx}`}>
                <i
                  className="pi pi-circle-fill"
                  style={{
                    color:
                      sup.suppressionType === "NOT_PAID"
                        ? "#ef4444"
                        : "#22c55e",
                    fontSize: "8px",
                  }}
                />
                <span className="flex-grow-1">
                  {resolveRulesetName(sup.rulesetId)}
                </span>
                <Dropdown
                  value={sup.suppressionType}
                  options={suppressionTypeOptions}
                  onChange={(e) =>
                    handleSuppressionTypeChange(row.id, idx, e.value)
                  }
                  className="w-21rem"
                />
                <Button
                  icon="pi pi-times"
                  className="p-button-text p-button-sm p-button-danger"
                  onClick={() => handleRemoveSuppression(row.id, idx)}
                />
              </SuppressionRow>
            )
          )}
          {availableForSuppression.length > 0 && (
            <Dropdown
              value={null}
              options={availableForSuppression}
              onChange={(e) => handleAddSuppression(row.id, e.value)}
              placeholder="+ Add Suppression"
              className="w-full mt-2"
            />
          )}
        </div>

        <div className="col-6">
          <h4 className="mt-0 mb-2">Suppressed By</h4>
          {suppressedBy.length === 0 ? (
            <span className="text-500">
              No other rules suppress this trigger.
            </span>
          ) : (
            suppressedBy.map((other) => (
              <div key={other.id} className="mb-1">
                <Badge value={other.name} severity="info" className="mr-1" />
                <span className="text-sm text-500">
                  Priority {other.priority}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  // --- Loading state ---

  if (!isCreateMode && isLoadingRules) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <ItemBar>
        <div className="flex align-items-center gap-3">
          <Button
            icon="pi pi-arrow-left"
            className="p-button-text"
            onClick={handleBack}
          />
          <h2 className="m-0">
            {isCreateMode
              ? "New Hierarchy Ruleset"
              : `Hierarchy Ruleset: ${providerName} + ${payerName}`}
          </h2>
        </div>
      </ItemBar>

      {isCreateMode && (
        <div className="block bg-white border-round p-4 mb-4">
          <h3 className="mt-0 mb-3">Provider and Payer</h3>
          <div className="formgrid grid">
            <div className="field col-6">
              <label className="font-bold text-sm mb-2 block">Provider</label>
              <Dropdown
                value={selectedProviderId}
                options={providerOptions}
                onChange={(e) => {
                  setSelectedProviderId(e.value);
                  setShowAddForm(false);
                }}
                placeholder="Select Provider"
                filter
                className="w-full"
              />
            </div>
            <div className="field col-6">
              <label className="font-bold text-sm mb-2 block">Payer</label>
              <Dropdown
                value={selectedPayerId}
                options={payerOptions}
                onChange={(e) => {
                  setSelectedPayerId(e.value);
                  setShowAddForm(false);
                }}
                placeholder="Select Payer"
                filter
                className="w-full"
              />
            </div>
          </div>
        </div>
      )}

      {hasSelection && (
        <div className="block bg-white border-round p-4 mb-4">
          <div className="flex justify-content-between align-items-center mb-3">
            <h3 className="m-0">Rules</h3>
            {!showAddForm && (
              <Button
                label="+ Add Rule"
                className="p-button-outlined p-button-sm"
                onClick={handleOpenAddForm}
              />
            )}
          </div>

          {localRules.length > 0 && (
            <DataTable
              value={localRules}
              expandedRows={expandedRows}
              onRowToggle={(e) => setExpandedRows(e.data)}
              rowExpansionTemplate={rowExpansionTemplate}
              dataKey="id"
              size="small"
              sortField="priority"
              sortOrder={1}
            >
              <Column expander style={{ width: "3em" }} />
              <Column field="name" header="Name" body={nameBodyTemplate} />
              <Column
                field="priority"
                header="Priority"
                body={priorityBodyTemplate}
                style={{ width: "120px" }}
              />
              <Column
                field="active"
                header="Active"
                body={activeBodyTemplate}
                style={{ width: "100px" }}
              />
              <Column
                header=""
                body={actionsBodyTemplate}
                style={{ width: "60px" }}
              />
            </DataTable>
          )}

          {localRules.length === 0 && !showAddForm && (
            <div className="text-500 text-center py-4">
              No hierarchy rules yet. Click &quot;+ Add Rule&quot; to create
              one.
            </div>
          )}

          {showAddForm && (
            <div className="border-1 border-round border-300 p-3 mt-3">
              <div className="font-bold text-sm mb-3">Add New Rule</div>
              {availableForNewRule.length === 0 ? (
                <div className="flex align-items-center justify-content-between">
                  <span className="text-500">
                    All rulesets for this provider-payer pair already have
                    hierarchy rules.
                  </span>
                  <Button
                    label="Close"
                    className="p-button-sm p-button-outlined"
                    onClick={handleCancelAddForm}
                  />
                </div>
              ) : (
                <div className="formgrid grid align-items-end">
                  <div className="field col-6 mb-0">
                    <label className="font-bold text-sm mb-2 block">
                      Trigger Ruleset
                    </label>
                    <Dropdown
                      value={newRuleRulesetId}
                      options={availableForNewRule}
                      onChange={(e) => setNewRuleRulesetId(e.value)}
                      placeholder="Select ruleset"
                      className="w-full"
                      emptyMessage="No available rulesets"
                    />
                  </div>
                  <div className="field col-3 mb-0">
                    <label className="font-bold text-sm mb-2 block">
                      Priority
                    </label>
                    <InputNumber
                      value={newRulePriority}
                      onValueChange={(e) => setNewRulePriority(e.value ?? null)}
                      min={1}
                      className="w-full"
                    />
                  </div>
                  <div className="field col-3 mb-0 flex gap-2">
                    <Button
                      label="Create"
                      className="p-button-sm"
                      onClick={handleCreateRule}
                      disabled={!newRuleRulesetId || !newRulePriority}
                      loading={isCreating}
                    />
                    <Button
                      label="Cancel"
                      className="p-button-sm p-button-outlined"
                      onClick={handleCancelAddForm}
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {!isCreateMode && localRules.length > 0 && (
        <ItemBar>
          <div className="flex flex-1 justify-content-end gap-2">
            <Button
              label="Cancel"
              className="p-button-outlined"
              onClick={handleCancel}
              disabled={!isDirty}
            />
            <Button
              label="Save Changes"
              onClick={handleSave}
              loading={isSaving}
              disabled={!isDirty}
            />
          </div>
        </ItemBar>
      )}
    </>
  );
}
